import { cn } from "@/lib/utils";
import { Label } from "@radix-ui/react-label";
import { DateTime } from "luxon";
import React from "react";
import { Await, useSearchParams } from "react-router-dom";
import { Checkbox } from "../ui/checkbox";
import { Combobox } from "../ui/combobox";
import DateRangePicker from "../ui/date-range-picker";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Filter } from "./types";
import DateTimeRangePicker from "@/components/ui/datetime-range-picker";

interface ObjectFilterProps extends React.HTMLAttributes<HTMLDivElement> {
    filter: Filter;
}

export function ObjectQuickFilter({ filter, className }: ObjectFilterProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentSelectValue = searchParams.get(filter.field_name);

    const update_search_params = (value: string | null | undefined) => {
        let _searchParams = new URLSearchParams(searchParams);
        const _value = filter.transformerFn ? filter.transformerFn(value) : value;
        if (_value) {
            _searchParams.set(filter.field_name, _value);
        } else {
            _searchParams.delete(filter.field_name);
        }
        if (_searchParams.toString() != searchParams.toString()) {
            _searchParams.set("page", "1"); // Reset pagination
            setSearchParams(_searchParams);
        }
    };

    if ((filter.type ?? "text") == "text") {
        return (
            <div className={cn("flex h-[36px] items-center", className, filter.className)}>
                <Input
                    id={`${filter.field_name}`}
                    name={filter.field_name}
                    value={searchParams.get(filter.field_name) || undefined}
                    placeholder={filter.name}
                    onChange={e => update_search_params(e.target.value)}
                    className="max-w-sm"
                />
            </div>
        );
    } else if (filter.type == "select") {
        const toggleSelectValue = (value: string) => {
            update_search_params(currentSelectValue === value ? null : value);
        };

        const renderSelectOption = (option: { value: string; label?: string }, index: number) => (
            <SelectItem
                key={`${option.value}_${index}`}
                value={option.value}
                onPointerDown={event => {
                    if (currentSelectValue === option.value) {
                        event.preventDefault();
                        toggleSelectValue(option.value);
                    }
                }}
            >
                {option.label ?? option.value}
            </SelectItem>
        );

        return (
            <div className={cn("flex h-[36px] items-center gap-2", className, filter.className)}>
                <Select
                    key={`${filter.field_name}-${currentSelectValue ?? ""}`}
                    value={currentSelectValue ?? ""}
                    onValueChange={toggleSelectValue}
                >
                    <SelectTrigger id={`${filter.field_name}`} className="max-w-sm bg-white">
                        <SelectValue placeholder={filter.name} />
                    </SelectTrigger>
                    <SelectContent>
                        {filter.optionsFn ? (
                            <React.Suspense>
                                <Await resolve={filter.optionsFn()}>
                                    {(options: { value: string; label?: string }[]) => options.map(renderSelectOption)}
                                </Await>
                            </React.Suspense>
                        ) : (
                            (filter.options || []).map(renderSelectOption)
                        )}
                    </SelectContent>
                </Select>
            </div>
        );
    } else if (filter.type == "combobox") {
        return (
            <div className={cn("flex h-[36px] items-center", className, filter.className)}>
                <Combobox
                    name={filter.field_name}
                    options={filter.options}
                    optionsUrl={filter.optionsUrl}
                    optionFormatter={filter.optionFormatter}
                    value={searchParams.get(filter.field_name) || undefined}
                    onValueChange={v => update_search_params(v)}
                    placeholder={filter.name}
                    className="w-full justify-between"
                    multiple
                />
            </div>
        );
    } else if (filter.type == "checkbox") {
        return (
            <div className={cn("flex h-[36px] items-center space-x-2 rounded border px-3", className, filter.className)}>
                <Label className="flex items-center space-x-2 text-xs">
                    <Checkbox checked={Boolean(searchParams.has(filter.field_name))} onCheckedChange={checked => update_search_params(checked ? (filter.inverted ? "0" : "1") : null)} />{" "}
                    <div>{filter.name}</div>
                </Label>
            </div>
        );
    } else if (filter.type === "date-range") {
        const value = searchParams.get(filter.field_name) || undefined;
        const [fromString, toString] = value?.split(",") ?? [];
        const from = fromString ? DateTime.fromISO(fromString) : undefined;
        const to = toString ? DateTime.fromISO(toString) : undefined;
        const range = from && to ? { from, to } : undefined;
        return (
            <div className={cn("grid w-full items-center gap-1.5", className, filter.className)}>
                <DateRangePicker
                    range={range}
                    onRangeChange={range => {
                        if (range && range.from && range.to) {
                            if (range.from.isValid && range.to.isValid) {
                                update_search_params(`${range.from.toISODate() ?? ""},${range.to.toISODate() ?? ""}`);
                            }
                        } else {
                            update_search_params(null);
                        }
                    }}
                    placeholder="Sélectionner une plage de dates"
                />
            </div>
        );
    } else if (filter.type === "datetime-range") {
        const value = searchParams.get(filter.field_name) || undefined;
        const range = value
            ? {
                  from: DateTime.fromISO(value.split(",")[0]),
                  to: value.split(",")[1] ? DateTime.fromISO(value.split(",")[1]) : DateTime.fromISO(value.split(",")[0])
              }
            : undefined;
        return (
            <div className={cn("grid w-full items-center gap-1.5", className, filter.className)}>
                <DateTimeRangePicker
                    range={range}
                    onRangeChange={range => {
                        if (range && range.from && range.to) {
                            if (range.from.isValid && range.to.isValid) {
                                update_search_params(`${range.from.toISO()},${range.to.toISO()}`);
                            }
                        } else {
                            update_search_params(null);
                        }
                    }}
                    placeholder="Sélectionner une plage de dates et heures"
                    className="w-full"
                />
            </div>
        );
    }
}
